import { AdUnit } from '@pubstack/common/src/adunit'
import { BidderSeat, expandObjectBidParams, isCrossInventoryServerSeat, isSparkForPubPublisherSeat, SPARK_FOR_PUB_BID_PARAM_SPECS } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog, BidParamSpec, Platform } from '@pubstack/common/src/bidderCatalog'
import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { canSiteCarryAdUnit, SITE_AD_UNIT_ENV_COMPATIBILITY, Site, SiteProduct, SiteProductFilter, SiteStatus } from '@pubstack/common/src/site'
import { Product } from '~/hooks/useProduct'

/**
 * Which inventory a bidder seat's mapping covers.
 *
 * Both halves of the answer live here rather than inside the mapping component, because two screens
 * ask them: the spreadsheet, which lists the medias and their ad units, and the seat header, whose
 * "mapped on n/m medias" counts the same medias. Split apart, they drifted — a seat mapped across both
 * products read "mapped on 5/3 medias" against a denominator still counting one.
 */

/** Every status but `inactive`: an archived media has nothing left to map. */
const MAPPABLE_SITE_STATUSES: SiteStatus[] = ['to-configure', 'out-of-sync', 'to-deploy', 'up-to-date']

export type MappingSiteFilters = {
  pbsStandaloneStatus?: SiteStatus[]
  admStatus?: SiteStatus[]
  siteProduct?: SiteProductFilter
  mediaProduct?: SiteProduct[]
}

/**
 * The medias a seat can be mapped on, as filters for the site list.
 *
 * A `node` seat is Pubstack's Prebid Server bidding for the whole scope, so it covers every media
 * activated on either product — One's included, which is what `siteProduct: 'any'` asks for. The
 * publisher half of a Spark For Pub pair answers the same way, bidding through that same server; its
 * internal twin does not, being keyed by `placementId` rather than by site×adUnit. Every other seat
 * stays inside its own product, which is what it has always done.
 *
 * `'any'` is deliberately not "no filter": that would also return the Analytics medias, which deploy
 * nothing and hold no ad unit, so a row on one could never mean anything.
 *
 * The seat is optional because the header asks this while the seat is still loading; without one the
 * product's own filter is the safe answer, since it is what every seat but `node` ends up with anyway.
 *
 * The status is asked for alongside `mediaProduct`, and the pair is the point: a status on its own narrows
 * nothing here. `StackStats.refreshMediaStatus` writes *both* products' statuses on every media it
 * touches, falling through to `up-to-date` where a product holds no stack — so a Node media carries a
 * live `admStatus` and a One media a live `pbsStandaloneStatus`, and `admStatus: [...]` alone answers
 * "every media in the scope". Only the sort hid it: mapped medias come first, so the foreign medias sat
 * past the first page until someone searched one by name.
 *
 * `mediaProduct` is `siteProductOf` in Mongo — the single product a media runs on, the same tie-break the
 * Product column makes. Deliberately not `siteProduct`, which the media list uses: that says "on this
 * product, or on none yet", and the Analytics medias it lets through deploy nothing, so a mapping row on
 * one could never mean anything.
 */
export const mappingSiteFilters = (bidderSeat: Pick<BidderSeat, 'type' | 'isInternal'> | undefined, product: Product): MappingSiteFilters =>
  bidderSeat && isCrossInventoryServerSeat(bidderSeat)
    ? { siteProduct: 'any' }
    : product === 'pbs'
      ? { mediaProduct: ['pbs'], pbsStandaloneStatus: MAPPABLE_SITE_STATUSES }
      : { mediaProduct: ['adm'], admStatus: MAPPABLE_SITE_STATUSES }

/**
 * An ad unit on the mapping screen, tagged with the collection it came from.
 *
 * `BidderSeatPage` sets the tag where the two lists are concatenated, the last point that still knows.
 * It used to be read off One's ad units carrying no `env`, which a Node one predating the field matched too.
 */
export type MappableAdUnit = (AdUnit & { product: 'adm' }) | (PbsAdUnit & { product: 'pbs' })

/**
 * The ad units a seat puts on screen, one list per collection the seat actually queried.
 *
 * A disabled query still answers from the cache: `useAdUnits` hands back whatever an earlier page left
 * under its key, so a seat that never asked for Node's ad units can still be holding them after the user
 * visited a node seat first. Those would reach `generateMappings` as One's, which reads `devices` — a field
 * a Node ad unit does not have. Each list is therefore dropped unless its own query was enabled.
 */
export const mappableAdUnits = (pbs: { adUnits: PbsAdUnit[]; enabled: boolean }, adm: { adUnits: AdUnit[]; enabled: boolean }): MappableAdUnit[] => [
  ...(pbs.enabled ? pbs.adUnits : []).map((adUnit) => ({ ...adUnit, product: 'pbs' as const })),
  ...(adm.enabled ? adm.adUnits : []).map((adUnit) => ({ ...adUnit, product: 'adm' as const })),
]

/**
 * Whether a media can be mapped on this ad unit, out of the ad units the seat put on screen.
 *
 * Only a cross-inventory seat holds both collections at once, and only there does "whose ad unit is
 * this?" arise — `canSiteCarryAdUnit` answers it from the media's activation flags, so a One media
 * offers One's web ad units and a Node media Node's, on the same seat.
 *
 * Every other seat was handed one product's ad units to begin with, so asking the flags again could
 * only ever subtract: `mappingSiteFilters` has already scoped the medias to the seat's product, and what
 * is left to subtract is a media the product filter calls One's while `oneRunsOnSite` refuses it — a legacy
 * both-flagged in-app one, which would lose every row. Those keep the environment check alone, which is
 * what they have always applied — One's ad units passing it outright, as the absence test used to let them.
 */
export const canMediaCarryMappedAdUnit = (bidderSeat: Pick<BidderSeat, 'type' | 'isInternal'>, site: Site, adUnit: MappableAdUnit): boolean =>
  // `?.` on the lookup: a media stored before `env` existed has none, and indexing the table with
  // `undefined` throws — which blanks the whole mapping spreadsheet rather than dropping one row.
  isCrossInventoryServerSeat(bidderSeat)
    ? canSiteCarryAdUnit(site, adUnit.product === 'adm' ? { product: 'adm' } : { product: 'pbs', env: adUnit.env })
    : adUnit.product === 'adm' || (SITE_AD_UNIT_ENV_COMPATIBILITY[site.env]?.includes(adUnit.env) ?? false)

/**
 * The bid params a seat's rows are edited with, on every screen that maps it.
 *
 * The publisher owns one param on their half of a Spark For Pub pair, the `isEnabled` toggle; the real SSP
 * params belong to the Spark team's half, which edits them against the catalog like any seat. Every other
 * seat edits the catalog's params for the platform it bids from, with each `object` namespace replaced by
 * the custom keys the seat declared under it.
 */
export const bidParamSpecsOfSeat = (
  bidderSeat: Pick<BidderSeat, 'type' | 'isInternal' | 'extraObjectBidParams'>,
  catalogBidder: Pick<BidderCatalog, 'bidParams'>,
  platform: Platform
): BidParamSpec[] =>
  isSparkForPubPublisherSeat(bidderSeat)
    ? SPARK_FOR_PUB_BID_PARAM_SPECS
    : expandObjectBidParams(catalogBidder.bidParams, bidderSeat).filter((spec) => !spec.platform || spec.platform.includes(platform))
